import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Grid,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { Save, Person, Lock, Language, Schedule, Logout, SwapHoriz, AccountCircle } from '@mui/icons-material';
import Cookies from 'js-cookie';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, isMultiTenantMode } from '../config/api';
import { useNavigate } from 'react-router-dom';
import TimezoneSelector from '../components/TimezoneSelector';

const AccountSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { user, updateProfile, logout, clearEnvironmentSelection } = useAuth();
  const navigate = useNavigate();
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    name: '',
    email: ''
  });
  
  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Preferences state
  const [preferences, setPreferences] = useState({
    language: 'nl',
    timezone: 'Europe/Amsterdam'
  });
  
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [preferencesMessage, setPreferencesMessage] = useState(null);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || ''
      });
    }

    // Load preferences from localStorage
    const savedLanguage = localStorage.getItem('language') || 'nl';
    const savedTimezone = localStorage.getItem('timezone') || 'Europe/Amsterdam';
    
    setPreferences({
      language: savedLanguage,
      timezone: savedTimezone
    });
  }, [user]);

  const handleProfileChange = (field) => (event) => {
    setProfileData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handlePreferenceChange = (field) => (value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogout = () => {
    logout();
  };

  const handleSwitchEnvironment = () => {
    clearEnvironmentSelection();
    navigate('/');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token') || Cookies.get('auth_token');
      const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profileData.name,
          email: profileData.email
        })
      });
      
      if (response.ok) {
        const updatedUser = await response.json();
        if (updateProfile) {
          updateProfile(updatedUser);
        }
        setMessage({ type: 'success', text: 'Profielgegevens succesvol bijgewerkt' });
      } else {
        const errorData = await response.json();
        let errorText = 'Er is een fout opgetreden';
        
        // Handle both string and object error responses
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorText = errorData.detail;
          } else if (typeof errorData.detail === 'object' && errorData.detail.message) {
            errorText = errorData.detail.message;
            // If there are specific errors, add them to the message
            if (errorData.detail.errors && Array.isArray(errorData.detail.errors)) {
              errorText += ': ' + errorData.detail.errors.join(', ');
            }
          }
        }
        
        setMessage({ type: 'error', text: errorText });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ type: 'error', text: 'Netwerkfout bij het bijwerken van profielgegevens' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordMessage(null);
    
    // Validatie
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Nieuwe wachtwoorden komen niet overeen' });
      setPasswordLoading(false);
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Nieuw wachtwoord moet minimaal 6 karakters lang zijn' });
      setPasswordLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem('token') || Cookies.get('auth_token');
      const response = await fetch(`${API_CONFIG.centralBackendURL}/api/v1/user/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });
      
      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Wachtwoord succesvol gewijzigd' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        const errorData = await response.json();
        let errorText = 'Er is een fout opgetreden';
        
        // Handle both string and object error responses
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorText = errorData.detail;
          } else if (typeof errorData.detail === 'object' && errorData.detail.message) {
            errorText = errorData.detail.message;
            // If there are specific errors, add them to the message
            if (errorData.detail.errors && Array.isArray(errorData.detail.errors)) {
              errorText += ': ' + errorData.detail.errors.join(', ');
            }
          }
        }
        
        setPasswordMessage({ type: 'error', text: errorText });
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordMessage({ type: 'error', text: 'Netwerkfout bij het wijzigen van wachtwoord' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    setPreferencesLoading(true);
    setPreferencesMessage(null);
    
    try {
      // Save language preference
      localStorage.setItem('language', preferences.language);
      if (preferences.language !== i18n.language) {
        i18n.changeLanguage(preferences.language);
      }
      
      // Save timezone preference
      localStorage.setItem('timezone', preferences.timezone);
      
      setPreferencesMessage({ type: 'success', text: 'Voorkeuren succesvol opgeslagen' });
      
      // Reload page to apply language changes
      if (preferences.language !== i18n.language) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Preferences update error:', error);
      setPreferencesMessage({ type: 'error', text: 'Fout bij het opslaan van voorkeuren' });
    } finally {
      setPreferencesLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        <Person sx={{ mr: 1, verticalAlign: 'bottom' }} />
        Accountinstellingen
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(event, newValue) => setTabValue(newValue)}>
          <Tab 
            icon={<Person />} 
            label="Profiel & Voorkeuren" 
            iconPosition="start"
          />
          <Tab 
            icon={<Lock />} 
            label="Wachtwoord" 
            iconPosition="start"
          />
          {isMultiTenantMode() && (
            <Tab 
              icon={<SwapHoriz />} 
              label="Omgeving wisselen" 
              iconPosition="start"
            />
          )}
          <Tab 
            icon={<Logout />} 
            label="Uitloggen" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab 1: Profiel & Voorkeuren */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Profielgegevens */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Profielgegevens
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {message && (
                  <Alert severity={message.type} sx={{ mb: 2 }}>
                    {message.text}
                  </Alert>
                )}
                
                <Box component="form" onSubmit={handleProfileSubmit}>
                  <TextField
                    fullWidth
                    label="Naam"
                    value={profileData.name}
                    onChange={handleProfileChange('name')}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label="E-mailadres"
                    value={profileData.email}
                    onChange={handleProfileChange('email')}
                    margin="normal"
                    type="email"
                    required
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Voorkeuren */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Language sx={{ mr: 1, verticalAlign: 'bottom' }} />
                  Voorkeuren
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {preferencesMessage && (
                  <Alert severity={preferencesMessage.type} sx={{ mb: 2 }}>
                    {preferencesMessage.text}
                  </Alert>
                )}
                
                <Box component="form" onSubmit={handlePreferencesSubmit}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="language-select-label">Taal</InputLabel>
                    <Select
                      labelId="language-select-label"
                      value={preferences.language}
                      label="Taal"
                      onChange={(e) => handlePreferenceChange('language')(e.target.value)}
                      renderValue={(selected) => {
                        const languages = {
                          'nl': { flag: '🇳🇱', name: 'Nederlands' },
                          'en': { flag: '🇬🇧', name: 'English' }
                        };
                        const lang = languages[selected];
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{lang?.flag}</span>
                            <span>{lang?.name}</span>
                          </Box>
                        );
                      }}
                    >
                      <MenuItem value="nl">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>🇳🇱</span>
                          <span>Nederlands</span>
                        </Box>
                      </MenuItem>
                      <MenuItem value="en">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <span>🇬🇧</span>
                          <span>English</span>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TimezoneSelector
                    value={preferences.timezone}
                    onChange={handlePreferenceChange('timezone')}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={preferencesLoading ? <CircularProgress size={20} /> : <Save />}
                    disabled={preferencesLoading}
                    sx={{ mt: 2 }}
                  >
                    {preferencesLoading ? 'Opslaan...' : 'Voorkeuren opslaan'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Wachtwoord wijzigen */}
      {tabValue === 1 && (
        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Lock sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Wachtwoord wijzigen
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {passwordMessage && (
              <Alert severity={passwordMessage.type} sx={{ mb: 2 }}>
                {passwordMessage.text}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                label="Huidig wachtwoord"
                type="password"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange('currentPassword')}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Nieuw wachtwoord"
                type="password"
                value={passwordData.newPassword}
                onChange={handlePasswordChange('newPassword')}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Bevestig nieuw wachtwoord"
                type="password"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange('confirmPassword')}
                margin="normal"
                required
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={passwordLoading ? <CircularProgress size={20} /> : <Save />}
                disabled={passwordLoading}
                sx={{ mt: 2 }}
              >
                {passwordLoading ? 'Wijzigen...' : 'Wachtwoord wijzigen'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Omgeving wisselen (alleen in multi-tenant mode) */}
      {isMultiTenantMode() && tabValue === 2 && (
        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <SwapHoriz sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Omgeving wisselen
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Wissel naar een andere electrolyzer omgeving. Je wordt teruggeleid naar de omgeving selectie pagina.
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<SwapHoriz />}
              onClick={handleSwitchEnvironment}
              size="large"
              sx={{ py: 1.5 }}
            >
              Omgeving wisselen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Uitloggen */}
      {tabValue === (isMultiTenantMode() ? 3 : 2) && (
        <Card sx={{ maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <Logout sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Uitloggen
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Uitloggen uit je account. Je wordt teruggeleid naar de login pagina.
            </Typography>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<Logout />}
              onClick={handleLogout}
              size="large"
              sx={{ py: 1.5 }}
            >
              Uitloggen
            </Button>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default AccountSettingsPage; 